<template>
  <LoadingModal v-if="loading"/>

  <div class="pageContent" v-else>
    <div class="detailBox">
      <div class="filters">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.role">
                    <option value selected>filtrovat dle role</option>
                    <option value="supervisor">vedoucí práce</option>
                    <option value="reader">oponent práce</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.user">
                    <option value selected>filtrovat dle autora</option>
                    <option
                      v-for="user in users"
                      :key="user.username"
                      :value="user.username"
                    >{{ user.name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.field">
                    <option value selected>filtrovat dle předmětu</option>
                    <option
                      v-for="field in fields"
                      :key="field.shorthand"
                      :value="field.shorthand"
                    >{{ field.title }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.uploaded">
                    <option value selected>filtrovat dle stavu práce</option>
                    <option value="true">nahrána</option>
                    <option value="false">nenahrána</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.report">
                    <option value selected>filtrovat dle stavu posudku</option>
                    <option value="true">vyplněn</option>
                    <option value="false">nevyplněn</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="theses.length">
        <div class="detailSection" v-for="thesis in theses" :key="thesis.origin.username">
          <h2 class="is-2 is-title" v-if="thesis.title">{{ thesis.title }}</h2>
          <h2 class="is-2 is-title is-blank" v-else>Maturitní práce bez názvu</h2>
          <h6 class="is-6 is-subtitle">{{ thesis.origin.fullName }} — {{ thesis.field.title }}</h6>
          <div class="level">
            <div class="level-left">
              <div class="field is-grouped">
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">role</span>
                    <span
                      class="tag is-light"
                    >{{ thesis.supervisor === $store.getters.username ? 'vedoucí práce' : 'oponent práce' }}</span>
                  </div>
                </div>

                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">stav práce</span>
                    <span class="tag is-success" v-if="thesis.uploaded">nahrána</span>
                    <span class="tag is-warning" v-else>nenahrána</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <button
                  class="btn spaceless isCarnation"
                  @click="toggleUploadModal(thesis.uuid)"
                  v-if="thesis && thesis.supervisor === $store.getters.username && !thesis.uploaded"
                >
                  <icon type="upload" size="16"/>Nahrát práci
                </button>

                <button
                  class="btn spaceless isBlue isTranslucent"
                  v-else-if="thesis && thesis.supervisor !== $store.getters.username && !thesis.uploaded"
                >
                  <icon type="edit-3" size="16"/>Napsat posudek
                </button>

                <button
                  class="btn spaceless isBlue"
                  @click="toggleModal(thesis.supervisor === $store.getters.username, thesis.uuid)"
                  v-else-if="thesis.uploaded && !thesis.report"
                >
                  <icon type="edit-3" size="16"/>Napsat posudek
                </button>

                <button
                  class="btn spaceless isTurquoise"
                  @click="goToReport(
                    thesis.supervisor === $store.getters.username ? 'supervisor' : 'reader',
                    thesis.uuid
                    )"
                  v-else
                >
                  <icon type="eye" size="16"/>Zobrazit posudek
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <em v-else>Nastaveným filtrům neodpovídají žádné maturitní práce.</em>

      <div class="modal" :class="{ 'is-active': reportModalShown }">
        <div class="modal-background"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p
              class="modal-card-title"
              v-if="onStep === 0"
            >Hodnocení maturitní práce v základních oblastech</p>
            <p class="modal-card-title" v-else-if="onStep === 1">Zdůvodnění hodnocení</p>
            <p class="modal-card-title" v-else-if="onStep === 2">Otázky k obhajobě</p>
            <p class="modal-card-title" v-else-if="onStep === 3">Rekapitulace posudku</p>
            <p class="modal-card-title" v-else></p>
            <button class="btn spaceless isGray isCollapsed" @click="toggleModal(null, null)">
              <icon type="x" size="16"/>
            </button>
          </header>
          <section class="modal-card-body" :class="{'isFlex': onStep === 4}">
            <div v-if="onStep === 0">
              <div class="control" v-for="(category, index) in assessmentCategories" :key="index">
                <strong class="slider-ahead">{{ category.description }}</strong>
                <div class="slider-container">
                  <input
                    class="slider is-fullwidth"
                    step="1"
                    min="0"
                    max="3"
                    type="range"
                    v-model="category.points"
                  >
                  <div class="signal-container">
                    <button
                      class="btn spaceless isSignal"
                      :class="`isSignal--${getSignalClass(category.points)}`"
                    >{{ category.points }}</button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="onStep === 1">
              <textarea class="textarea" rows="12" v-model="assessmentDescription"></textarea>
            </div>

            <div v-else-if="onStep === 2">
              <div class="field" v-for="question in assessmentQuestions" :key="question.id">
                <div class="control">
                  <input v-model="question.text" class="input" type="text">
                </div>
              </div>

              <div class="level">
                <div class="level-left"></div>
                <div class="level-right">
                  <div class="level-item">
                    <button class="btn spaceless isTurquoise" @click="addQuestion()">
                      <icon type="plus" size="16"/>Přidat otázku
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="recap" v-else-if="onStep === 3">
              <h3>Hodnocení maturitní práce v jednotlivých oblastech</h3>

              <p v-for="(category, index) in assessmentCategories" :key="index">
                {{ category.description }}:
                <strong>{{ declinePoints(category.points) }}</strong>
              </p>

              <h3 v-if="assessmentDescription">Zdůvodnění hodnocení</h3>

              <p>{{ assessmentDescription }}</p>

              <h3>Otázky k obhajobě</h3>

              <p
                v-for="question in assessmentQuestions"
                :key="question.id"
                class="recap-question"
                v-if="question.text"
              >
                <strong>{{ question.text }}</strong>
              </p>
            </div>

            <div class="result" v-else-if="onStep === 4">
              <div class="result-box" v-if="failed">
                <div class="result-circle result-failed">
                  <icon type="x" size="32"/>
                </div>
                <h3>Vyskytla se chyba.</h3>
                <h6>Posudek nebyl odeslán.</h6>
              </div>

              <div class="result-box" v-else>
                <div class="result-circle result-success">
                  <icon type="check" size="32"/>
                </div>
                <h3>Posudek byl úspěšně odeslán.</h3>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <div class="level" v-if="onStep < 4">
              <div class="level-left">
                <div class="level-item">
                  <button
                    class="btn spaceless isGray"
                    :class="{
                      'isTranslucent': onStep === 0,
                      'isDisabled': onStep === 0
                    }"
                    @click="step(-1)"
                  >
                    <icon type="arrow-left" size="16"/>Zpět
                  </button>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <button
                    class="btn spaceless isBlue isReversed"
                    v-if="onStep !== 3"
                    @click="step(1)"
                  >Další
                    <icon type="arrow-right" size="16"/>
                  </button>
                  <button class="btn spaceless isGreen" @click="pushReport()" v-else>
                    <icon type="save" size="16"/>Odeslat posudek
                  </button>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>

      <UploadModal
        :shown="uploadModalShown"
        :uuid="uploadUuid"
        @closeSelf="toggleUploadModal(null)"
        @uploadThesis="uploadThesis"
      />
    </div>
  </div>
</template>

<script>
import { parseDate } from '../util';
import { postUrl, postReport, fetchThesesTeacher, fetchThesisReports } from '../http';
import LoadingModal from '../components/LoadingModal';
import UploadModal from '../components/UploadModal';

export default {
  data() {
    return {
      uploadUuid: null,
      uploadModalShown: false,
      reportModalShown: false,
      currentThesisUuid: null,
      isSupervisor: null,
      loading: true,
      data: null,
      failed: false,
      sentReport: false,
      onStep: 0,
      filters: {
        role: '',
        user: '',
        field: '',
        uploaded: '',
        report: ''
      },
      assessmentQuestions: [
        {
          id: 0,
          text: ''
        },
        {
          id: 1,
          text: ''
        },
        {
          id: 2,
          text: ''
        }
      ],
      assessmentDescription: '',
      assessmentExtra: [
        {
          description: 'Spolupráce s vedoucím práce',
          points: '0'
        }
      ],
      assessmentCategories: [
        {
          description: 'Kvalita pojednání tématu práce',
          points: '0'
        },
        {
          description: 'Věcná správnost',
          points: '0'
        },
        {
          description: 'Vlastní přínos žáka',
          points: '0'
        },
        {
          description: 'Dosažení cíle práce',
          points: '0'
        },
        {
          description: 'Práce se zdroji',
          points: '0'
        },
        {
          description: 'Formální zpracování, grafická úprava, jazyková a stylistická správnost',
          points: '0'
        }
      ]
    };
  },

  computed: {
    users() {
      return this.data
        .map(thesis => thesis.origin)
        .map(origin => ({ name: origin.fullName.split(' '), username: origin.username }))
        .sort((a, b) => a.name[1].localeCompare(b.name[1]))
        .map(origin => ({ name: origin.name.join(' '), username: origin.username }));
    },

    fields() {
      return this.data.map(thesis => thesis.field);
    },

    theses() {
      let theses = this.data;

      if (this.filters.role) {
        if (this.filters.role === 'supervisor') {
          theses = theses.filter(thesis => thesis.supervisor === this.$store.getters.username);
        } else {
          theses = theses.filter(thesis => thesis.reader === this.$store.getters.username);
        }
      }

      if (this.filters.user) {
        theses = theses.filter(thesis => thesis.origin.username === this.filters.user);
      }

      if (this.filters.field) {
        theses = theses.filter(thesis => thesis.field.shorthand === this.filters.field);
      }

      if (this.filters.field) {
        theses = theses.filter(thesis => thesis.field.shorthand === this.filters.field);
      }

      if (this.filters.uploaded) {
        if (this.filters.uploaded === 'true') {
          theses = theses.filter(thesis => thesis.uploaded);
        } else {
          theses = theses.filter(thesis => !thesis.uploaded);
        }
      }

      if (this.filters.report) {
        if (this.filters.report === 'true') {
          theses = theses.filter(thesis => thesis.report);
        } else {
          theses = theses.filter(thesis => !thesis.report);
        }
      }

      return theses;
    }
  },

  components: {
    LoadingModal,
    UploadModal
  },

  methods: {
    toggleUploadModal(uuid) {
      this.uploadUuid = uuid;
      this.uploadModalShown = !this.uploadModalShown;
    },

    async uploadThesis(payload) {
      await postUrl(payload);
      await location.reload(true);
    },

    toggleModal(isSupervisor, uuid) {
      this.isSupervisor = isSupervisor;

      if (uuid) {
        this.currentThesisUuid = uuid;
      }

      if (!this.reportModalShown && isSupervisor) {
        this.assessmentCategories = [...this.assessmentCategories, ...this.assessmentExtra];
      } else if (this.reportModalShown) {
        this.onStep = 0;
        this.failed = false;
        this.isSupervisor = null;
        this.assessmentCategories = this.assessmentCategories
          .filter(category => category.description !== 'Spolupráce s vedoucím práce')
          .map(category => ({
            description: category.description,
            points: '0'
          }));

        if (this.sentReport) {
          this.sentReport = false;
          location.reload(true);
        }
      }

      this.reportModalShown = !this.reportModalShown;
    },

    step(n) {
      if ((this.onStep === 0 && n < 0) || (this.onStep === 4 && n > 0)) {
        return;
      }

      this.onStep += n;
    },

    async pushReport() {
      const assessmentPoints = this.assessmentCategories.map(crit => parseInt(crit.points) || 0);
      const assessmentQuestions = this.assessmentQuestions
        .map(question => question.text)
        .filter(question => question !== '');

      if (assessmentPoints.length === 6) {
        assessmentPoints.push(null);
      }

      await postReport({
        thesisUuid: this.currentThesisUuid,
        origin: this.$store.getters.username,
        isSupervisor: this.isSupervisor,
        assessmentPoints: assessmentPoints || '',
        assessmentQuestions,
        assessmentText: this.assessmentDescription
      }).catch(e => {
        this.failed = true;
      });

      this.currentThesisUuid = null;
      this.isSupervisor = null;
      this.sentReport = true;

      this.step(1);
    },

    declinePoints(points) {
      return `${points} bod${points === '0' ? 'ů' : points === '1' ? '' : 'y'}`;
    },

    addQuestion() {
      this.assessmentQuestions.push({
        id: this.assessmentQuestions.length,
        text: ''
      });
    },

    getSignalClass(points) {
      const pointsToColor = {
        '0': 'isRed',
        '1': 'isOrange',
        '2': 'isYellow',
        '3': 'isLime'
      };

      return pointsToColor[points];
    },

    prepReportView(author, uuid) {
      const thesis = this.data.filter(thesis => thesis.uuid === uuid)[0];

      const metadata = {
        thesisAuthor: thesis.origin.fullName,
        author: this.$store.getters.fullUserName,
        className: thesis.origin.class,
        field: thesis.field.title || null,
        title: thesis.title || null
      };

      this.$store.commit('saveReport', {
        report: thesis.report,
        metadata,
        author
      });
    },

    goToReport(author, uuid) {
      this.prepReportView(author, uuid);
      this.$router.push({ path: 'report', query: { author } });
    }
  },

  async created() {
    try {
      const username = this.$store.getters.username;

      const {
        data: { data }
      } = await fetchThesesTeacher(username);

      this.data = [...data.isSupervisor, ...data.isReader];

      for (let thesis of this.data) {
        const {
          data: { data }
        } = await fetchThesisReports(thesis.uuid);

        let sourceReport;

        if (this.$store.getters.username === thesis.supervisor) {
          sourceReport = data.supervisor;
        } else {
          sourceReport = data.reader;
        }

        if (sourceReport) {
          const report = {
            points: [
              sourceReport.assessment_1,
              sourceReport.assessment_2,
              sourceReport.assessment_3,
              sourceReport.assessment_4,
              sourceReport.assessment_5,
              sourceReport.assessment_6,
              sourceReport.assessment_7
            ],
            text: sourceReport.assessment_text,
            questions: sourceReport.questions
          };

          thesis.report = report;
        } else {
          thesis.report = null;
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style lang="sass" scoped>
@import '../styles/buttons.sass'

h2.is-blank
  opacity: .25

h2.is-title
  margin-bottom: .5rem

h6
  color: gray
  font-weight: normal

.detailBox
  background: white
  box-shadow: 0 2px 3px rgba(10,10,10, .1), 0 0 0 1px rgba(10,10,10, .1)
  padding: 3.25rem 2rem
  margin: 1.75rem 0

.detailSection
  padding: 1.5rem 0

  &:not(:first-child)
    border-top: 1px solid #f5f5f5

.modal-card
  height: 660px

.modal-card-title
  margin-bottom: 0 !important

.modal-card-foot .level
  width: 100%

textarea
  width: 100%
  resize: none

.recap
  & h3:not(:first-child)
    margin-top: 1.5rem

  & p
    margin-bottom: .5rem

  &-question:before
    content: '•'
    margin-right: .5rem

.slider-ahead
  display: block

.signal-container
  display: flex
  justify-content: flex-end
  align-items: center

.slider-container
  display: flex

  & > *:first-child
    flex: 1 0 87%

  & > *:last-child
    flex: 1 1 auto

.filters
  padding: 0 0 1.5rem

  & .select:not(.is-multiple):not(.is-loading)::after
    border-color: #5182EC

.modal-card-body.isFlex
  display: flex
  justify-content: center
  align-items: center

.result
  height: 100%

  &, &-box, &-circle
    display: flex
    justify-content: center
    align-items: center

  &-box
    flex-direction: column

  &-circle
    width: 64px
    height: 64px
    border-radius: 50%
    color: white
    margin-bottom: 1rem

  &-success
    background: linear-gradient(to top, #23d160, #2de36d)
    box-shadow: 0px 2px 10px 3px rgba(#23d160 ,.2)

  &-failed
    background: #FF4949
    box-shadow: 0px 2px 10px 3px rgba(darken(#FF4949, 5%), .2)

.btn.isTranslucent
  cursor: not-allowed
</style>
